'use client';

import { DatePicker } from '@koda/koda-components/molecules/DatePicker';
import NumberField from '@koda/koda-components/molecules/NumberField';
import TextField from '@koda/koda-components/molecules/TextField';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import { Past5Years } from './consts/dateIntervals';
import {
  createRecreationalRunsDefaults,
  RECREATIONAL_RUNS_CONTENT_PATH,
  recreationalRunsSchema,
} from './recreationalRuns.Schema';

export default function RecreationalRunsComponent(): ReactElement {
  const licenseCode = LicenseTypes.RecreationalRuns;

  const { values, setField, getError, costCalculated, showErrors } = useLicenseBusiness(licenseCode, {
    schema: recreationalRunsSchema,
    defaults: createRecreationalRunsDefaults,
  });
  const t = useTranslations(RECREATIONAL_RUNS_CONTENT_PATH);

  return (
    <>
      <TextField
        label={t('raceName.label')}
        name="raceName"
        placeholder={t('raceName.placeholder')}
        required
        styleVariant="border"
        disabled={costCalculated}
        type="text"
        value={values.raceName}
        onChange={e => setField('raceName', e.target.value)}
        error={showErrors ? getError('raceName') : undefined}
        data-error-field={!!(showErrors && getError('raceName'))}
        validated={!!values.raceName && !getError('raceName')}
      />

      <DatePicker
        label={t('date.label')}
        name="date"
        placeholder={t('date.placeholder')}
        disabled={costCalculated}
        value={values.date ? new Date(values.date) : undefined}
        onChange={date => setField('date', date ? dayjs(date).format('YYYY-MM-DD') : '')}
        error={showErrors ? getError('date') : undefined}
        data-error-field={!!(showErrors && getError('date'))}
        validated={!!values.date && !getError('date')}
        initialVisibleValue={new Date()}
        interval={Past5Years}
      />

      <NumberField
        label={t('participantCount.label')}
        name="participantCount"
        placeholder={t('participantCount.placeholder')}
        required
        formatThousands
        disabled={costCalculated}
        value={values.participantCount}
        onChange={e => setField('participantCount', parseInt(e.target.value))}
        error={showErrors ? getError('participantCount') : undefined}
        data-error-field={!!(showErrors && getError('participantCount'))}
        validated={!!values.participantCount && !getError('participantCount')}
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
    </>
  );
}
